import { useContext, useEffect, useId, useRef, useState } from "react"
import { GeoJSONSource, ImageSource, Map as MaplibreMap } from "maplibre-gl"
import { MapInteractionState, MapitStateContext, actionSetMapInteractionState } from "src/states/MapitState"
import { MapFacadeMaplibre, MitLayerHandleMaplibre } from "src/managers/MapFacadeMaplibre"
import { FeatureLayer } from "src/managers/WmsLayerFunc"
import * as Turf from "@turf/turf";
import jsPDF from "jspdf"
import { Localization, SettingsManager } from "@viamap/viamap2-common"
import { ProgressBar, Spinner } from "react-bootstrap"
import ActionSelector from './ActionSelector'
import { VectorLayer } from "src/managers/VectorLayerFunc"
import { IgnoreBlock, LegendEditorPlot } from "./LegendEditorPlot"
import { MapitUtils } from "src/managers/MapitUtils"
import { DataDivisionType, LayerInfo, LayerType } from "src/common/managers/Types"
import { GenerateGeom } from "src/managers/GenerateGeom"
import { ProtectedFeature } from "./ProtectedFeature"
import { Feature } from "src/states/ApplicationStateFeatures"
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal"
import { GlassButton, GlassButtonDropdown } from "./GlassButtons"
import { GICButton, GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "./GlassInput"
import { GlassFoldUdBox, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents"
import { canvasPDFLIB, drawDrawing, drawFoldMarkers, maxWebGLSize, paperToSize, PlotLayerCornerArr, PromiseImage, saveWithTimeStampt } from "src/functions/PaperCanvas"
import { GeojsonWFS } from "src/managers/WFSGeojsonLayerFunc"
import { useWindowContext } from "src/WindowManager/useWindowContext"
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState"
import { DynamicDataDrivenLayer } from "src/managers/DynamicDataDrivenLayer"
import { ActionTracker } from "src/common/managers/ActionTracker"
import { PDFDocument, PDFImage } from "pdf-lib"
import { MitStorage } from "src/managers/MitStorage"
import { MapitLayerFactory } from "./MapitLayerFactory"
import { ApplicationStateContext } from "src/states/ApplicationState"
import { AppMode } from "src/states/ApplicationStateFeatureMapping"
import { Setting2 } from "iconsax-react"
import { MIBIcon } from "src/componentsUtils/MIBIcon"
import { getJpbBufferPointLegendLayerId, JPB_BUFFER_POINT_PIN_COLOR } from "src/modules/JPBModule/jpbBufferPinLegend"

const moduses = ["Standard Plot", "Seperate Layer Plot", "Matrikel Info Plot"] as const;
const JPB_LAYER_PREFIX = "jpb-";
const JPB_ICON_CONFIGS = [
  { id: "jpb-house-checked", size: 26, iconScale: 1 },
  { id: "jpb-house-unchecked", size: 26, iconScale: 1 },
  { id: "jpb-house-locked", size: 26, iconScale: 1 },
] as const;

type PlotScreenState = {
  modus: typeof moduses[number];
  format: string;
  fold: boolean;
  foldFormat: string;
  center: number[];
  bearing: number;
  scale: number;
  margin: number;
  title: string;
  description: string;
  showLegend: boolean;
  includeDate: boolean;
  legendIgnoreBlocks: IgnoreBlock[]
  logoImg: Promise<"" | HTMLImageElement>;
  arrowImg: Promise<"" | HTMLImageElement>;
  imgCache: { [key: string]: Promise<"" | HTMLImageElement> };
  dataLayer: LayerInfo[]
  legendImg: {
    img: Promise<"" | HTMLImageElement>;
    ftl: FeatureLayer;
    label?: string;
  }[];
  attrib: string[];
}

type Props = {
  managedWindow: ManagedWindow,
  callbackOnClose: () => void,
  externalSettings?: Partial<PlotScreenState>
}

function cloneJson<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

function resolveBeforeLayerId(
  sourceLayers: any[],
  currentLayerId: string,
  targetMap: MaplibreMap,
  copiedLayerIds: Set<string>
): string | undefined {
  const currentIdx = sourceLayers.findIndex((layer) => layer?.id === currentLayerId);
  if (currentIdx < 0) return undefined;

  for (let i = currentIdx + 1; i < sourceLayers.length; i++) {
    const candidateId = sourceLayers[i]?.id;
    if (!candidateId || copiedLayerIds.has(candidateId)) {
      continue;
    }
    if (targetMap.getLayer(candidateId)) {
      return candidateId;
    }
  }
  return undefined;
}

function applyCheckedFeatureStates(targetMap: MaplibreMap, sourceId: string, sourceData: any) {
  const features = sourceData?.features;
  if (!Array.isArray(features)) return;

  features.forEach((feature: any) => {
    const id = feature?.id ?? feature?.properties?.documentID;
    if (id === undefined || id === null) return;
    const checked = Boolean(feature?.properties?.checked);
    try {
      targetMap.setFeatureState({ source: sourceId, id }, { checked, hover: false });
    } catch {
      // Best-effort feature state copy.
    }
  });
}

async function ensurePlotJPBBuildingIcons(targetMap: MaplibreMap, sourceMap: MaplibreMap) {
  for (const config of JPB_ICON_CONFIGS) {
    if (targetMap.hasImage(config.id)) {
      continue;
    }

    try {
      if (sourceMap.hasImage(config.id)) {
        const sourceImage = sourceMap.getImage(config.id);
        if (sourceImage && !targetMap.hasImage(config.id)) {
          targetMap.addImage(config.id, sourceImage as any, { pixelRatio: 2 });
          if (targetMap.hasImage(config.id)) {
            continue;
          }
        }
      }
    } catch (error) {
      console.warn(`PlotScreen: failed to copy JPB icon "${config.id}"`, error);
    }

    try {
      const dataUrl = await GenerateGeom.reactIconMarker(MIBIcon, {
        icon: "house",
        iconColor: "#ffffff",
        size: config.size,
        iconScale: config.iconScale,
        strokeWidth: 1.1
      });
      const loadedImage = await targetMap.loadImage(dataUrl);
      if (!targetMap.hasImage(config.id)) {
        targetMap.addImage(config.id, loadedImage.data, { pixelRatio: 2 });
      }
    } catch (error) {
      console.warn(`PlotScreen: failed to generate JPB icon "${config.id}"`, error);
    }
  }
}

async function copyJPBLayersToPlotMap(sourceMap: MaplibreMap, targetMap: MaplibreMap) {
  const sourceStyle = sourceMap.getStyle();
  if (!sourceStyle?.layers || !sourceStyle?.sources) return;

  const sourceLayers = sourceStyle.layers;
  const jpbLayers = sourceLayers.filter((layer: any) => layer?.id?.startsWith(JPB_LAYER_PREFIX));
  if (!jpbLayers.length) return;

  const copiedLayerIds = new Set(jpbLayers.map((layer: any) => layer.id));
  const sourceIdsToCopy = new Set<string>();
  const geojsonDataBySourceId: Record<string, any> = {};

  jpbLayers.forEach((layer: any) => {
    if (typeof layer?.source === "string") {
      sourceIdsToCopy.add(layer.source);
    }
  });

  sourceIdsToCopy.forEach((sourceId) => {
    if (targetMap.getSource(sourceId)) return;

    const styleSourceSpec = (sourceStyle.sources as any)?.[sourceId];
    if (!styleSourceSpec) return;

    const nextSourceSpec: any = cloneJson(styleSourceSpec);
    if (nextSourceSpec.type === "geojson") {
      const liveSource = sourceMap.getSource(sourceId) as any;
      const liveData = liveSource?._data;
      if (liveData) {
        nextSourceSpec.data = cloneJson(liveData);
      }
      geojsonDataBySourceId[sourceId] = nextSourceSpec.data;
    }

    try {
      targetMap.addSource(sourceId, nextSourceSpec);
    } catch (error) {
      console.warn(`PlotScreen: failed to copy JPB source "${sourceId}"`, error);
    }
  });

  await ensurePlotJPBBuildingIcons(targetMap, sourceMap);

  jpbLayers.forEach((layer: any) => {
    if (!layer?.id || targetMap.getLayer(layer.id)) return;

    const layerSpec = cloneJson(layer);
    const beforeLayerId = resolveBeforeLayerId(sourceLayers, layer.id, targetMap, copiedLayerIds);

    try {
      if (beforeLayerId) {
        targetMap.addLayer(layerSpec, beforeLayerId);
      } else {
        targetMap.addLayer(layerSpec);
      }
    } catch (error) {
      console.warn(`PlotScreen: failed to copy JPB layer "${layer.id}"`, error);
    }
  });

  Object.entries(geojsonDataBySourceId).forEach(([sourceId, sourceData]) => {
    applyCheckedFeatureStates(targetMap, sourceId, sourceData);
  });
}


const _mm = 8 //pixels

//Global Feature to hold Square feature
let GB_feature: null | any = null


// MARK: Component Plotscreen
export const PlotScreen = (props: Props) => {

  let { dispatch: windowDispatch } = useWindowContext();
  let [editLegend, setEditLegend] = useState(false);
  let { state: applicationState } = useContext(ApplicationStateContext);
  let remember = SettingsManager.getSystemSetting("plot.remember", false);


  let [loading, _setLoading] = useState(true);
  let [loadingBar, setLoadingBar] = useState({ taskDone: 0, TaskToDo: 0 })
  let [showCustomScale, setShowCustomScale] = useState(false);
  let [tempScaleValue, setTempScaleValue] = useState("0");

  let [activeTool, setActiveTool] = useState("none")
  let activeToolRef = useRef("none")

  const debug = SettingsManager.getSystemSetting("debuggingShowExtraDebuggingPlot", false)

  function setLoading(b) {
    if (debug) {
      console.debug("Loading Plot", b)
    }
    _setLoading(b)
  }

  useEffect(() => {
    activeToolRef.current = activeTool
  }, [activeTool])



  let { state: mapitState, dispatch: mapDispatch } = useContext(MapitStateContext);

  let uniqueMapID = useId()
  let uniqueCanvasID = useId()
  let uniqueLegendID = useId();
  let mapRef = useRef<MapFacadeMaplibre | null>(null)
  let downLoadRef = useRef<HTMLAnchorElement | null>(null)

  let [lState, setLState] = useState<PlotScreenState>({
    modus: "Standard Plot",

    //force Reload
    format: (remember && MitStorage.getValue("PlotScreenFormat")) || SettingsManager.getSystemSetting("plot.format", "A3L", true),

    //force Map update
    center: mapitState.map.getCenter().get2dArr().toReversed(),
    bearing: mapitState.map.getBearing(),
    scale: parseInt((0.75 * 200000000 * Math.exp(-0.703 * mapitState.map.getZoom())).toLocaleString("da-dk", { maximumSignificantDigits: 2 }).replace(".", "")), // Magic 

    //force Canvas update
    fold: (remember && MitStorage.getValue("PlotScreenFold")) || SettingsManager.getSystemSetting("plot.fold", false, true),
    foldFormat: (remember && MitStorage.getValue("PlotScreenFoldFormat")) || SettingsManager.getSystemSetting("plot.foldFormat", "A4", true),
    margin: (remember && MitStorage.getValue("PlotScreenMargin")) || SettingsManager.getSystemSetting("plot.margin", 6, true), //in mm, - 1 mm = 8pixe, truels
    title: (remember && MitStorage.getValue("PlotScreenTitle")) || SettingsManager.getSystemSetting("plot.title", "Default Map Title", true),
    description: (remember && MitStorage.getValue("PlotScreenDescription")) || SettingsManager.getSystemSetting("plot.description", "Default Description", true),
    showLegend: (remember && MitStorage.getValue("PlotScreenShowLegend")) || SettingsManager.getSystemSetting("plot.showLegend", true, true),
    includeDate: SettingsManager.getSystemSetting("plot.includeDate", true, true),
    legendIgnoreBlocks: [],
    dataLayer: Object.values(mapitState.layers || []),
    logoImg: PromiseImage(logoImage()), //Cache img
    arrowImg: PromiseImage(new URL("/Mapit/north.svg", import.meta.url)), //Cache img

    // LegendImg cache

    imgCache: {},

    // Attributions:
    attrib: [...new Set([...["viamap ApS"].filter((a) => a), ...mapitState.selectedFeatureLayerKeys.toReversed().map((a) => {
      let ftl = mapitState.featureLayers[a]
      return ftl?.layer?.["attrib"] || ""
    })])],
    ...props.externalSettings,
    legendImg: [...mapitState.selectedFeatureLayerKeys.toReversed().flatMap((a) => {
      let ftl = mapitState.featureLayers[a]
      if (ftl == undefined) {
        return []
      }
      if ((ftl?.layer as any)?.type === "vectorTile" || (ftl.layer as any)?.type === "geojsonWFS") {
        if ((ftl?.layer as any)?.legend) {
          return { ftl: ftl, img: PromiseImage((ftl.layer as any)?.legend) }
        }
        return { ftl: ftl, img: PromiseImage("") }
      }
      if (ftl.layer.layers.split(",").length > 1) {
        let url = ftl.getLegendURL();
        return ftl.layer.layers.split(",").map((a) => url.replace(ftl.layer.layers, a)).filter((a, b) => !ftl.layer.legendExclude?.includes(b)).map((imgUrl, idx) => ({
          ftl: ftl, img: PromiseImage(imgUrl),
          label: ftl.layer.legendLabels?.["da"]?.[idx]
        }))
      }
      return {
        ftl: ftl, img: PromiseImage(ftl.getLegendURL()),
        label: ftl.layer.legendLabels?.["da"]?.[0]
      }
    }), ...props.externalSettings?.legendImg || []]
  })


  useEffect(() => {
    mapDispatch(actionSetMapInteractionState(MapInteractionState.Override))
    return () => mapDispatch(actionSetMapInteractionState(MapInteractionState.Normal))
  }, [])

  useEffect(() => {
    GenerateLegend(
      document.getElementById(uniqueLegendID) as HTMLCanvasElement,
      lState
    )
    function DrawCanvas() {
      const canvas = document.getElementById(uniqueCanvasID) as HTMLCanvasElement;
      const legend = document.getElementById(uniqueLegendID) as HTMLCanvasElement;
      const ctx = canvas && canvas.getContext("2d");
      const img = (!loading && mapRef.current!.getCanvas()) || new Image();
      if (ctx) {
        drawDrawing(
          ctx,
          canvas,
          legend,
          img,
          lState
        )
        drawFoldMarkers(ctx, canvas, lState)
      }
    }
    DrawCanvas()


  }, [lState, loading])

  // Update CanvasMap
  useEffect(() => {
    setLoading(true);
    let rMap = mapitState.map.getMapPop();
    (rMap.getSource("CanvasPlotSource") as ImageSource)?.setCoordinates(PlotLayerCornerArr(lState.center, lState.bearing, lState.scale, lState.format).filter((a, idx) => idx < 4) as any);
    GB_feature = Turf.polygon([PlotLayerCornerArr(lState.center, lState.bearing, lState.scale, lState.format)]);
    ((rMap.getSource("Square") as GeoJSONSource | undefined)?.setData(GB_feature));
    if (mapRef.current) {
      const Corners = PlotLayerCornerArr(lState.center, 0, lState.scale, lState.format) // Zero Rotation, Happens in two steps
      mapRef.current.once("moveend", () => { //Make Sure FitBounds happens
        mapRef.current?.getMapPop().setBearing(lState.bearing) // Seconds step
        const rot = async () => {
          while (mapRef.current?.customLoading() === true) {
            await new Promise((resolve) => setTimeout(resolve, 100))
          }
          if (mapRef.current?.getMapPop().loaded()) {
            setLoading(false)
          } else {
            mapRef.current?.once?.("idle", async () => {
              setLoading(false)
            });
          }
        }
        if (mapRef.current?.getMapPop().loaded()) {
          rot()
        } else {
          mapRef.current?.once("idle", rot)
        }
      })
      mapRef.current.getMapPop().fitBounds(
        Corners.filter((a, idx) => idx % 2 == 0) as any
      )
    }

    // return () => {}
  }, [lState.bearing, lState.center, lState.scale])

  useEffect(() => {
    setLoading(true);
    const CornersUpRight = PlotLayerCornerArr(lState.center, 0, lState.scale, lState.format)
    let max = maxWebGLSize(mapitState.map.getMapPop().getCanvas().getContext("webgl2") || mapitState.map.getMapPop().getCanvas().getContext("webgl"));
    let options = {
      mapStyle: mapitState.mapInfo.mapStyle,
      canvasContextAttributes: {
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance',
      },
      interactive: false,
      hash: false,
      maxCanvasSize: [max, max] as [number, number],
      bounds: CornersUpRight.filter((a, idx) => idx % 2 == 0) as any,
      bearing: lState.bearing,
      pixelRatio: 2,
    } as Partial<maplibregl.MapOptions>
    let MapFac = new MapFacadeMaplibre(uniqueMapID, false, options)

    let rMap = mapitState.map.getMapPop()

    let urlToLabels = Object.values(mapitState.featureLayers).map((a) => ({
      urlIdent: a.layer.host + "|" + a.layer.layers,
      label: a.layer?.translationTable ? Localization.getTextSpecificTable(a.layer.label, a.layer?.translationTable) : a.layer.label, x: Math.random(), y: Math.random()
    }))

    MapFac?.setUrlToLabels?.(urlToLabels);

    MapFac.getMapPop().setBearing(lState.bearing)
    rMap.addSource("CanvasPlotSource", { ...CanvasSource(lState.center, lState.bearing, lState.scale, lState.format, uniqueCanvasID) as any })
    let squareSource = PlotSource(lState.center, lState.bearing, lState.scale, lState.format)
    GB_feature = (squareSource as any).data
    rMap.addSource("Square", squareSource)
    rMap.addLayer(CanvasStyle())
    // rMap.addLayer(PlotStyleLine("SquareL","Square"))
    rMap.addLayer(PlotStyleSpec("SquareF", "Square"))
    const moveEnter = (e) => MouseEnter(e, "SquareF", activeToolRef, { move: (a, b) => setLState((c) => ({ ...c, center: [c.center[0] + a, c.center[1] + b] })), rotate: (ang) => setLState((c) => ({ ...c, bearing: c.bearing + ang })), setLoading: () => setLoading(true) })
    rMap.on("mouseenter", "SquareF", moveEnter)
    mapRef.current = MapFac
    mapRef.current.getMapPop().setPixelRatio(2)
    const plotMap = mapRef.current.getMapPop();

    const populatePlotMap = async () => {
      mapitState.selectedFeatureLayerKeys.forEach((a) => {
        if (mapitState.featureLayers[a] instanceof VectorLayer || mapitState.featureLayers[a] instanceof GeojsonWFS) {
          mapRef.current?.addVectorLayer(mapitState.featureLayers[a] as VectorLayer);
          return
        }
        if (mapitState.featureLayers[a] instanceof DynamicDataDrivenLayer && mapRef.current) {
          mapRef.current.addDynamicLayer(mapitState.featureLayers[a] as DynamicDataDrivenLayer);
          return
        }
        let layer = (mapitState.featureLayers[a] as FeatureLayer);
        if (layer) {
          mapRef.current?.addFeatureLayer(a, layer)
        }
      })
      lState.dataLayer.forEach((a) => {
        if (a.visible && a.handle) {
          mapRef.current?.addLayer(a.handle)
        } else if (a.visible && !a.handle) {
          let mapinfo = mapitState.mapInfo
          let { layerHandle } = MapitLayerFactory.createADataLayer(MapFac, a, mapinfo, {})
          if (layerHandle) {
            mapRef.current?.addLayer(layerHandle)
          }
        }
      })
      mapitState.printLayers.forEach((a) => {
        switch (a.type) {
          case "Vector":
            mapRef.current?.addVectorLayer(a.handle);
            break;
          case "Raster":
            mapRef.current?.addFeatureLayer((a.handle as FeatureLayer).layer.label, (a.handle as FeatureLayer))
            break;
          case "MitLayer":
            let layer = a.handle as MitLayerHandleMaplibre
            if (layer)
              mapRef.current?.addLayer(layer)
            break;
        }
      })

      await copyJPBLayersToPlotMap(rMap, plotMap);
      plotMap.triggerRepaint();
    };

    const mapReadyForDraw = (async () => {
      if (plotMap.loaded()) {
        await populatePlotMap();
        return;
      }
      await new Promise<void>((resolve) => {
        plotMap.once('load', () => {
          void (async () => {
            await populatePlotMap();
            resolve();
          })();
        });
      });
    })();

    (async () => {
      await mapReadyForDraw;
      while (mapRef.current?.customLoading() === true) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
      if (mapRef.current?.getMapPop().loaded()) {
        setLoading(false);
      } else {
        mapRef.current?.once?.("idle", () => setLoading(false));
      }
    })();

    return () => {
      const rMap = mapitState.map.getMapPop() as maplibregl.Map
      if (rMap.getLayer("CanvasPlot")) rMap.removeLayer("CanvasPlot")
      if (rMap.getSource("CanvasPlotSource")) rMap.removeSource("CanvasPlotSource")
      if (rMap.getLayer("SquareL")) rMap.removeLayer("SquareL")
      if (rMap.getLayer("SquareF")) rMap.removeLayer("SquareF")
      if (rMap.getSource("Square")) rMap.removeSource("Square")
      rMap.off("mouseenter", "SquareF", moveEnter);
      mapRef?.current?.getMapPop()?.remove?.()
    }
  }, [lState.format])

  function setText(key, value: string) {
    let matches = value.match(/(^https:\/.+\.(?:svg|png|jpeg))/gm)
    let x = {}
    matches?.forEach((a) => {
      if (!lState.imgCache[a])
        x = { ...x, [a]: PromiseImage(a) }
    })
    setLState((a) => ({ ...a, imgCache: { ...a.imgCache, ...x } }))

    setLState((a) => ({ ...a, [key]: value }))
  }


  async function printPDF() {
    let paper = ("" + lState.format).toLowerCase()

    const doc = new jsPDF({
      format: paper.slice(0, - 1),
      orientation: paper.at(-1) as any
    });

    let paperS = paperToSize(lState.format)
    doc.addImage(document.getElementById(uniqueCanvasID) as HTMLCanvasElement, 0, 0, paperS.width, paperS.height)
    doc.autoPrint()
    window.open(doc.output('bloburl'), '_blank');
  }

  function waitIdle() {
    return new Promise<boolean>((resolve) => {
      if (mapRef.current) {
        mapRef.current.once("idle", () => resolve(true))
      } else {
        resolve(false)
      }
    });
  }

  async function SuperPDF(compression: unknown = 0.9) {
    let paper = lState.format
    if (mapitState.selectedFeatureLayerKeys.length === 0) {
      return canvasPDFLIB(uniqueCanvasID, mapitState.map.getMapPop().getSource("CanvasPlotSource") as ImageSource, lState.format, 0.9);
    }
    setLoadingBar({ taskDone: 0, TaskToDo: mapitState.selectedFeatureLayerKeys.length })


    const __doc = await PDFDocument.create();
    let page = __doc.addPage([paperToSize(paper).width * 2.83465, paperToSize(paper).height * 2.83465]);
    mapitState.selectedFeatureLayerKeys.forEach((a) => {
      if (mapitState.featureLayers[a] instanceof DynamicDataDrivenLayer) {
        mapRef.current?.changeDynamicVisibility(mapitState.featureLayers[a] as DynamicDataDrivenLayer, "none");
        return
      }
      if (mapitState.featureLayers[a] instanceof VectorLayer || mapitState.featureLayers[a] instanceof GeojsonWFS) {
        mapRef.current?.changeVectorVisibility(mapitState.featureLayers[a] as VectorLayer, "none");
        return
      }
      let layer = (mapitState.featureLayers[a] as FeatureLayer);
      if (layer) mapRef.current?.changeFeatureVisibility(a, "none")

    })
    let keys = [...mapitState.selectedFeatureLayerKeys]
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      let groupKey = [key]
      if (key.startsWith("\\")) {
        let uuid = key.split("\\")[1]
        groupKey = keys.filter((a) => a.includes(uuid))
      }


      for (let key of groupKey) {
        if (mapitState.featureLayers[key] instanceof DynamicDataDrivenLayer) {
          mapRef.current?.changeDynamicVisibility(mapitState.featureLayers[key] as DynamicDataDrivenLayer, "visible");
        } else if (mapitState.featureLayers[key] instanceof VectorLayer || mapitState.featureLayers[key] instanceof GeojsonWFS) {
          mapRef.current?.changeVectorVisibility(mapitState.featureLayers[key] as VectorLayer, "visible");
        } else {
          let layer = (mapitState.featureLayers[key] as FeatureLayer);
          if (layer) mapRef.current?.changeFeatureVisibility(key, "visible")
        }
      }



      await waitIdle()
      await CompletedDrawing({
        description: lState.description,
        attrib: [...new Set([...["viamap ApS", "OpenStreetMap"].filter((a) => a), ...[key].map((a) => {
          let ftl = mapitState.featureLayers[a]
          return ftl?.layer?.["attrib"] || ""
        })])],
        legendImg: groupKey.toReversed().flatMap((a) => {
          let ftl = mapitState.featureLayers[a]
          if ((ftl?.layer as any)?.type === "vectorTile" || (ftl.layer as any)?.type === "geojsonWFS") {
            if ((ftl.layer as any)?.legend) {
              return { ftl: ftl, img: PromiseImage((ftl.layer as any)?.legend) }
            }
            return { ftl: ftl, img: PromiseImage("") }
          }
          if (ftl?.layer.layers.split(",").length > 1) {
            let url = ftl.getLegendURL();
            return ftl.layer.layers.split(",").map((a) => url.replace(ftl.layer.layers, a)).filter((a, b) => !ftl.layer.legendExclude?.includes(b)).map((imgUrl, idx) => ({
              ftl: ftl, img: PromiseImage(imgUrl),
              label: ftl.layer.legendLabels?.["da"]?.[idx]
            }))
          }
          return {
            ftl: ftl, img: PromiseImage(ftl.getLegendURL?.()),
            label: ftl?.layer?.legendLabels?.["da"]?.[0]
          }
        }),
      })
      if (i !== 0) {
        page = __doc.addPage([paperToSize(paper).width * 2.83465, paperToSize(paper).height * 2.83465]);
      }
      let pdfImage: PDFImage
      let canvasElem = document.getElementById(uniqueCanvasID) as HTMLCanvasElement
      if (typeof compression === "number") {
        pdfImage = await __doc.embedJpg(canvasElem instanceof HTMLCanvasElement ? canvasElem.toDataURL("image/jpeg", compression) : "");
      } else {
        pdfImage = await __doc.embedPng(canvasElem instanceof HTMLCanvasElement ? canvasElem.toDataURL("image/png") : "");
      }
      page.drawImage(pdfImage, {
        x: 0,
        y: 0,
        width: paperToSize(paper).width * 2.83465,
        height: paperToSize(paper).height * 2.83465,
      });
      setLoadingBar({ taskDone: i + 1, TaskToDo: mapitState.selectedFeatureLayerKeys.length })
      for (let key of groupKey) {
        if (mapitState.featureLayers[key] instanceof DynamicDataDrivenLayer) {
          mapRef.current?.changeDynamicVisibility(mapitState.featureLayers[key] as DynamicDataDrivenLayer, "none");
        } else if (mapitState.featureLayers[key] instanceof VectorLayer || mapitState.featureLayers[key] instanceof GeojsonWFS) {
          mapRef.current?.changeVectorVisibility(mapitState.featureLayers[key] as VectorLayer, "none");

        } else {
          let layer = (mapitState.featureLayers[key] as FeatureLayer);
          if (layer) mapRef.current?.changeFeatureVisibility(key, "none")
        }
      }
      i = i + groupKey.length - 1
    }
    // doc.autoPrint()
    const pdfBytes = await __doc.save();
    const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    mapitState.selectedFeatureLayerKeys.forEach((a) => {
      if (mapitState.featureLayers[a] instanceof DynamicDataDrivenLayer) {
        mapRef.current?.changeDynamicVisibility(mapitState.featureLayers[a] as DynamicDataDrivenLayer, "visible");
        return
      }
      if (mapitState.featureLayers[a] instanceof VectorLayer || mapitState.featureLayers[a] instanceof GeojsonWFS) {
        mapRef.current?.changeVectorVisibility(mapitState.featureLayers[a] as VectorLayer, "visible");
        return
      }
      let layer = (mapitState.featureLayers[a] as FeatureLayer);
      if (layer) mapRef.current?.changeFeatureVisibility(a, "visible")
    })
    await waitIdle()
    await CompletedDrawing({})
    setLoadingBar({ taskDone: 0, TaskToDo: 0 })
    return url;
  }

  function SaveImg() {
    (downLoadRef.current as HTMLAnchorElement).download = saveWithTimeStampt("mapit-Plot") + ".png";
    (downLoadRef.current as HTMLAnchorElement).href = (document.getElementById(uniqueCanvasID) as HTMLCanvasElement).toDataURL();
    (downLoadRef.current as HTMLAnchorElement).click()
  }

  function UpdateLocation() {
    setLState((a) => ({
      ...a,
      center: mapitState.map.getCenter().get2dArr().toReversed(),
      bearing: mapitState.map.getBearing()
    }))
  }


  async function CompletedDrawing(overWritelState) {
    await GenerateLegend(
      document.getElementById(uniqueLegendID) as HTMLCanvasElement,
      { ...lState, ...overWritelState }
    )
    async function DrawCanvas() {
      const canvas = document.getElementById(uniqueCanvasID) as HTMLCanvasElement;
      const legend = document.getElementById(uniqueLegendID) as HTMLCanvasElement;
      const ctx = canvas && canvas.getContext("2d");
      const img = (!loading && mapRef.current!.getCanvas()) || new Image();
      if (ctx) {
        await drawDrawing(
          ctx,
          canvas,
          legend,
          img,
          { ...lState, ...overWritelState }
        )
        drawFoldMarkers(ctx, canvas, { ...lState, ...overWritelState })
      }
    }
    await DrawCanvas()
  }

  let scaleValues = [500, 1000, 2000, 2500, 5000, 7500, 10000, 12000, 12500, 15000, 20000, 25000, 50000];

  // MARK: Plotscreen return
  return (
    <>
      <AdvancedDragModal
        window={props.managedWindow}
        PosDefault={{ left: "100px", top: "50%", transform: "translateY(-50%)" }}
        title={Localization.getText("Plot")}
        onClose={() => windowDispatch(closeWindow(WindowId.PlotScreen))}
        subHeaderContent={applicationState.appMode == AppMode.Normal ? <ViewButtonIcon onClick={(() => {
          windowDispatch(openWindow(WindowId.UserSettingsEditor, { filter: (g: { key: string }) => g.key === "plot" }))
        })} title={Localization.getText("Settings")} >
          <Setting2 />
        </ViewButtonIcon> : <></>}
      >
        {editLegend && lState.modus === "Standard Plot" ?
          <LegendEditorPlot canvasId={uniqueLegendID} callBack={(next) => setLState((pre) => ({ ...pre, legendIgnoreBlocks: next }))} />
          : <></>}
        <ADModalBody>
          <GlassInputGroup>
            {/* <div className="Setting_Label">{Localization.getText("Plot modus")}</div> */}
            {/* <div className="Setting_Input"> */}
            {/* <select value={lState.modus} onChange={((e) => setLState((a) => ({...a, modus: (e.target.value as PlotScreenState["modus"])})))} > */}
            {/* <option value={"Standard Plot"}>{"Standard Plot"}</option> */}
            {/* <option value={"Seperate Layer Plot"}>{"Seperate Layer Plot"}</option> */}
            {/* <option value={"Matrikel Info Plot"}>{"Matrikel Info Plot"}</option> */}
            {/* </select> */}
            {/* </div> */}

            <GlassTextInputTransformer label={Localization.getText("Paper")}>
              <GlassInputControl>
                <select value={lState.format} onChange={((e) => {
                  MitStorage.setValue("PlotScreenFormat", e.target.value)
                  setLState((a) => ({ ...a, format: e.target.value }))
                })}>
                  <option value={"A0L"}>{Localization.getFormattedText("{size} Landscape", { size: "A0" })}</option>
                  <option value={"A0P"}>{Localization.getFormattedText("{size} Potrait", { size: "A0" })}</option>
                  <option value={"A1L"}>{Localization.getFormattedText("{size} Landscape", { size: "A1" })}</option>
                  <option value={"A1P"}>{Localization.getFormattedText("{size} Potrait", { size: "A1" })}</option>
                  <option value={"A2L"}>{Localization.getFormattedText("{size} Landscape", { size: "A2" })}</option>
                  <option value={"A2P"}>{Localization.getFormattedText("{size} Potrait", { size: "A2" })}</option>
                  <option value={"A3L"}>{Localization.getFormattedText("{size} Landscape", { size: "A3" })}</option>
                  <option value={"A3P"}>{Localization.getFormattedText("{size} Potrait", { size: "A3" })}</option>
                  <option value={"A4L"}>{Localization.getFormattedText("{size} Landscape", { size: "A4" })}</option>
                  <option value={"A4P"}>{Localization.getFormattedText("{size} Potrait", { size: "A4" })}</option>
                  <option value={"A5L"}>{Localization.getFormattedText("{size} Landscape", { size: "A5" })}</option>
                  <option value={"A5P"}>{Localization.getFormattedText("{size} Potrait", { size: "A5" })}</option>
                  <option value={"A6L"}>{Localization.getFormattedText("{size} Landscape", { size: "A6" })}</option>
                </select>
              </GlassInputControl>
            </GlassTextInputTransformer>



            <GlassTextInputTransformer label={Localization.getText("Scale")}>
              <GlassInputControl>
                <GICButton onClick={() => {
                  setShowCustomScale((a) => {
                    return !a
                  })
                  if (!showCustomScale) {
                    setTempScaleValue(lState.scale + "")
                  }
                }}
                  title={Localization.getText("Write custom scale")}
                ><MIBIcon icon="edit" /></GICButton>
                {!showCustomScale ?
                  <select value={lState.scale} onChange={((e) => setLState((a) => ({ ...a, scale: parseInt(e.target.value) })))}>
                    {scaleValues.includes(lState.scale) ? <></> :
                      <option value={lState.scale} >1:{(lState.scale).toLocaleString()}</option>
                    }
                    {scaleValues.map((a) => {
                      return <option key={"val" + a} value={a} >1:{(a).toLocaleString("da-dk")}</option>
                    })}

                  </select>
                  :
                  <>
                    <input value={tempScaleValue} onBlur={() => { setLState((a) => ({ ...a, scale: parseInt(tempScaleValue.replace(".", "")) })) }} onChange={(e) => setTempScaleValue(e.target.value)} ></input>
                  </>
                }
              </GlassInputControl>
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Title")}>
              <GlassInputControl>
                <textarea onChange={(e) => {
                  MitStorage.setValue("PlotScreenTitle", e.target.value)
                  setText("title", e.target.value)
                }} value={lState.title}></textarea>
              </GlassInputControl>
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Text")}>
              <GlassInputControl>
                <textarea onChange={(e) => {
                  MitStorage.setValue("PlotScreenDescription", e.target.value)
                  setText("description", e.target.value)
                }} value={lState.description}></textarea>
              </GlassInputControl>
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Legend")}>
              <div className="Horizontal_Radio_Button_group">
                <button data-info={Localization.getText("Show")} className={lState.showLegend ? "active" : ""} onClick={() => {
                  MitStorage.setValue("PlotScreenShowLegend", true)
                  setLState((a) => ({ ...a, showLegend: true }))
                }} ><MIBIcon icon="show" /></button>
                <button data-info={Localization.getText("Hide")} className={!lState.showLegend ? "active" : ""} onClick={() => {
                  MitStorage.setValue("PlotScreenShowLegend", false)
                  setLState((a) => ({ ...a, showLegend: false }))
                }} ><MIBIcon icon="hide" /></button>
                <button data-info={Localization.getText("Edit")} style={{ marginLeft: "auto" }} onClick={() => setEditLegend((a) => !a)} ><MIBIcon icon="erase" /></button>
              </div>
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Action")}>
              <ActionSelector activeTool={activeTool} setActiveTool={setActiveTool} onCapture={() => UpdateLocation()} />
            </GlassTextInputTransformer>
          </GlassInputGroup>
          <GlassFoldUdBox
            variant="Small"
            title={Localization.getText("More Settings")}
            foldetUd={false}
          >
            <GlassInputGroup>
              <GlassTextInputTransformer label={Localization.getText("Margin")}>
                <GlassInputControl>
                  <input value={lState.margin} min={0} max={50} type="number" onChange={((e) => {
                    MitStorage.setValue("PlotScreenMargin", parseInt(e.target.value))
                    setLState((a) => ({ ...a, margin: parseInt(e.target.value) }))
                  })} ></input>
                  <span>mm</span>
                </GlassInputControl>
              </GlassTextInputTransformer>
              <GlassTextInputTransformer label={Localization.getText("Fold Markers")}>
                <div className="Horizontal_Radio_Button_group">
                  <button data-info={Localization.getText("Show")} className={lState.fold ? "active" : ""} onClick={() => {
                    MitStorage.setValue("PlotScreenFoldMarkers", true)
                    setLState((a) => ({ ...a, fold: true }))
                  }} ><MIBIcon icon="show" /></button>
                  <button data-info={Localization.getText("Hide")} className={!lState.fold ? "active" : ""} onClick={() => {
                    MitStorage.setValue("PlotScreenFoldMarkers", false)
                    setLState((a) => ({ ...a, fold: false }))
                  }} ><MIBIcon icon="hide" /></button>
                </div>
              </GlassTextInputTransformer>
              <GlassTextInputTransformer label={Localization.getText("Fold to")}>
                <GlassInputControl>

                  <select value={lState.foldFormat} onChange={((e) => {
                    MitStorage.setValue("PlotScreenFoldFormat", e.target.value)
                    setLState((a) => ({ ...a, foldFormat: e.target.value }))
                  })}>
                    <option value={"A0"}>A0</option>
                    <option value={"A1"}>A1</option>
                    <option value={"A2"}>A2</option>
                    <option value={"A3"}>A3</option>
                    <option value={"A4"}>A4</option>
                    <option value={"A5"}>A5</option>
                  </select>
                </GlassInputControl>
              </GlassTextInputTransformer>
            </GlassInputGroup>
          </GlassFoldUdBox>

        </ADModalBody>
        <ADModalFooter>
          {loadingBar.TaskToDo ? <ProgressBar now={loadingBar.taskDone / loadingBar.TaskToDo * 100} label={`${loadingBar.taskDone} / ${loadingBar.TaskToDo}`} style={{
            flex: 1,
            borderRadius: '5px',
            '--bs-progress-bar-bg': '#3ae580',
            '--bs-progress-height': '2rem'
          } as React.CSSProperties} /> :
            loading ? <Spinner size="sm" /> :
              <>
                <ProtectedFeature feature={Feature.MapPlotSuper} contentsIfNoAccess={<></>}>
                  <GlassButton title={Localization.getText("Seperate layer plot")} onClick={async () => {
                    ActionTracker.track("PlotScreen", "Plot", "S-PDF", {})
                    let url = await SuperPDF(1.0);
                    if (url) {
                      (downLoadRef.current as HTMLAnchorElement).download = saveWithTimeStampt("mapit-Plot") + ".pdf";
                      (downLoadRef.current as HTMLAnchorElement).href = url;
                      (downLoadRef.current as HTMLAnchorElement).click();
                      URL.revokeObjectURL((downLoadRef.current as HTMLAnchorElement).href)
                    }
                  }} >S-PDF</GlassButton>
                </ProtectedFeature>
                <GlassButton onClick={() => {
                  ActionTracker.track("PlotScreen", "Plot", "PNG", {})
                  SaveImg()
                }} >PNG</GlassButton>
                <ProtectedFeature feature={Feature.MapPlotCompress} contentsIfNoAccess={
                  <>
                    <GlassButton onClick={async () => {
                      ActionTracker.track("PlotScreen", "Plot", "PDF", {})
                      const url = await canvasPDFLIB(uniqueCanvasID, mapitState.map.getMapPop().getSource("CanvasPlotSource") as ImageSource, lState.format, 1.0);
                      (downLoadRef.current as HTMLAnchorElement).download = saveWithTimeStampt("mapit-Plot") + ".pdf";
                      (downLoadRef.current as HTMLAnchorElement).href = url;
                      (downLoadRef.current as HTMLAnchorElement).click();
                      URL.revokeObjectURL((downLoadRef.current as HTMLAnchorElement).href)
                    }} >PDF</GlassButton>
                  </>
                }>
                  <GlassButtonDropdown onSelect={async (e) => {
                    ActionTracker.track("PlotScreen", "Plot", "PDF", { quality: e })
                    canvasPDFLIB(uniqueCanvasID, mapitState.map.getMapPop().getSource("CanvasPlotSource") as ImageSource, lState.format, e).then((url) => {
                      (downLoadRef.current as HTMLAnchorElement).download = saveWithTimeStampt("mapit-Plot") + ".pdf";
                      (downLoadRef.current as HTMLAnchorElement).href = url;
                      (downLoadRef.current as HTMLAnchorElement).click();
                    })
                  }} placement="top-end" options={[
                    { text: "0.0", value: 0.0 },
                    { text: "0.1", value: 0.1 },
                    { text: "0.2", value: 0.2 },
                    { text: "0.3", value: 0.3 },
                    { text: "0.4", value: 0.4 },
                    { text: "0.5", value: 0.5 },
                    { text: "0.6", value: 0.6 },
                    { text: "0.7", value: 0.7 },
                    { text: "0.8", value: 0.8 },
                    { text: "0.9", value: 0.9 },
                    { text: "1.0", value: 1.0 },
                    { text: "Max", value: "PNG" },
                  ]} >PDF</GlassButtonDropdown>
                </ProtectedFeature>
                <GlassButton onClick={() => {
                  printPDF()
                  ActionTracker.track("PlotScreen", "Plot", "PRINT", {})
                  printPDF()
                }} >PRINT</GlassButton>
              </>
          }
        </ADModalFooter>
      </AdvancedDragModal >



      <a ref={downLoadRef} style={{ display: "none" }} href="" download >Click</a>
      <div className="CanvasCon" style={{ width: "420px", height: "594px", position: "absolute", backgroundColor: "white", zIndex: (debug ? 2 : -1), pointerEvents: "none", opacity: (debug ? 1 : 0) }} >
        <canvas id={uniqueCanvasID} width={paperToSize(lState.format).width * 8} height={paperToSize(lState.format).height * 8} style={{ width: "100%" }} />
      </div>
      <div className="CanvasCon" style={{ position: "absolute", left: "50%", backgroundColor: "white", zIndex: (debug ? 2 : -1), pointerEvents: "none", opacity: (debug ? 1 : 0) }} >
        <canvas id={uniqueLegendID} width={paperToSize(lState.format).width * 8} height={paperToSize(lState.format).height * 8} style={{ width: "100%" }} />
      </div>
      <div className="CanvasCon" style={{ width: "420px", height: "594px", position: "absolute", backgroundColor: "white", zIndex: (debug ? 1 : -1), pointerEvents: "none", opacity: (debug ? 1 : 0) }} >
        <div className="PlotScreen" id={uniqueMapID} style={{ width: paperToSize(lState.format).width * 4 + "px", height: paperToSize(lState.format).height * 4 + "px", position: "absolute", backgroundColor: "white", zIndex: 1, pointerEvents: "none", opacity: 1 }} />
      </div>
    </>
  );
}

function PlotSource(center, rot, scale, format): maplibregl.SourceSpecification {

  return {
    type: "geojson",
    data: Turf.polygon([PlotLayerCornerArr(center, rot, scale, format)])
  }
}

function CanvasSource(center, rot, scale, format, canvas): maplibregl.CanvasSourceSpecification {
  return {
    type: "canvas",
    canvas: canvas,
    coordinates: PlotLayerCornerArr(center, rot, scale, format).filter((a, idx) => idx < 4) as any,
    animate: true
  }
}

function CanvasStyle(): maplibregl.LayerSpecification {
  return {
    id: "CanvasPlot",
    source: "CanvasPlotSource",
    type: "raster"
  }
}

function PlotStyleSpec(id, source): maplibregl.LayerSpecification {
  return {
    id: id,
    type: "fill",
    source: source,
    paint: {
      "fill-color": "#fff0",
      "fill-outline-color": "grey",
    }
  }
}


function MouseEnter(e, layer, action, callBack) {
  let feature: undefined | any = undefined
  let center: undefined | any = undefined
  let start: (number | undefined)[] = [undefined, undefined];
  let end: (number | undefined)[] = [undefined, undefined];
  let startAngle: number | undefined = undefined;
  let endAngle: number | undefined = undefined;

  if (action.current == "move") {
    e.target.getCanvas().style.cursor = "move";
  }
  if (action.current == "rota") {
    let url = `url('${new URL("/cursor/rotate.svg", import.meta.url)}') 10 10 , auto`;
    e.target.getCanvas().style.cursor = url;
  }


  const mouseUp = (e) => {
    end = [e.lngLat.lng, e.lngLat.lat]
    endAngle = center && Turf.bearing(center, end as [number, number])
    if (action.current == "move" && end[0] && start[0] && end[1] && start[1]) {
      callBack.move(end[0] - start[0], end[1] - start[1])
    }
    if (action.current == "rota" && endAngle && startAngle) {
      callBack.rotate(endAngle - startAngle)
    }
    e.target.off("mouseup", mouseUp);
    e.target.off("mousemove", mouseMove)
  }

  const mouseMove = (e) => {
    let cur = [e.lngLat.lng, e.lngLat.lat]
    let curAngle = center && Turf.bearing(center, cur as [number, number])

    if (action.current == "move" && cur[0] && start[0] && cur[1] && start[1]) {
      let lngC = cur[0] - start[0]
      let latC = cur[1] - start[1]
      let newFeature = GB_feature && {
        ...feature,
        geometry: {
          ...feature.geometry,
          coordinates: [feature.geometry.coordinates[0].map((a) => [a[0] + lngC, a[1] + latC])]
        }
      }
      GB_feature = newFeature
      e.target.getSource("Square")?.setData(GB_feature);
      (e.target.getSource("CanvasPlotSource") as ImageSource)?.setCoordinates(newFeature.geometry.coordinates[0].filter((a, idx) => idx < 4))
    }
    if (action.current == "rota" && curAngle && startAngle) {
      let newFeature = feature && Turf.transformRotate(feature, curAngle - startAngle);
      GB_feature = newFeature
      e.target.getSource("Square")?.setData(GB_feature);
      (e.target.getSource("CanvasPlotSource") as ImageSource)?.setCoordinates(newFeature.geometry.coordinates[0].filter((a, idx) => idx < 4))
    }


  }

  const mouseDown = (e) => {
    feature = GB_feature
    // feature = e.features[0];
    center = Turf.center(feature);
    start = [e.lngLat.lng, e.lngLat.lat]
    startAngle = center && Turf.bearing(center, start as [number, number])
    e.preventDefault();
    callBack.setLoading?.()
    e.target.on("mouseup", mouseUp);
    e.target.on("mousemove", mouseMove)
  }

  //LAST cleaning
  const mouseLeave = () => {
    e.target.getCanvas().style.cursor = ""
    e.target.off("mouseleave", layer, mouseLeave);
    e.target.off("mousedown", layer, mouseDown);
  }

  if (action.current !== "none") {
    e.target.on("mousedown", layer, mouseDown);
    e.target.on("mouseleave", layer, mouseLeave);
  }
}

// MARK: LEGEND
async function GenerateLegend(
  canvas: HTMLCanvasElement,
  drawingOptions: PlotScreenState
) {

  let dataL = await dataLayerLegend(drawingOptions)

  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.font = "30px Arial";
  }
  const awaitedImages = await Promise.all(drawingOptions.legendImg.map(async (a) => ({ ...a, img: await a.img })))
  const legendWidth = Math.max(Math.max(...awaitedImages.map((a) => (a.img ? a.img.width : 0))), ctx?.measureText("Signaturforklaring").width || 0)
  let legendScaling = 1.5;
  let legendHeight = 4 + 55 + awaitedImages.reduce((a, b) => (b.img !== "" && b?.ftl?.layer && !("noLegend" in b.ftl.layer && b.ftl.layer.noLegend === true)) ? (((b.ftl.layer.legendCrop || b.img.height) * legendScaling) + (b.ftl?.layer?.label ? 25 : 0) + a) : a + (b.ftl?.layer?.label ? 25 : 0), 0)
  legendHeight = legendHeight ? legendHeight + 0 : legendHeight

  canvas.height = legendHeight + dataL.reduce((pre, cur) => pre + cur.height, 0);
  canvas.width = (15 + legendWidth) * legendScaling;

  if (!ctx) {
    return
  }

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, legendWidth, legendHeight)

  let currTop = 20
  ctx.fillStyle = "#000";

  ctx.font = "30px Arial";
  ctx.fillText("Signaturforklaring", 5, currTop + 20)

  // DATA Legend color
  currTop += 35
  dataL.forEach((a) => {
    currTop += DrawDataIcon(ctx, currTop, a.icon, a.layerInfo)
    ctx.font = "20px Arial";
    currTop += DrawColorByValue(ctx, currTop, a.valueColorLegend, a.layerInfo)
    currTop += DrawSizeByValue(ctx, currTop, a.valueSizeLegend, a.layerInfo)
  })

  // FEATURELAYER
  currTop = dataL.reduce((pre, cur) => pre + cur.height, 0) + 55;
  ctx.font = "20px Arial";

  let layerIcon = await svgToHtmlImage(GenerateGeom.MitLayer("black"))
  let firstWithoutLabel = true;
  awaitedImages.forEach((a) => {
    if (a.ftl.layer.label) {
      currTop += 25;
      ctx.font = "bold 18px Arial"
      if (layerIcon) ctx.drawImage(layerIcon, 15, currTop - 29, 22, 22);
      ctx.fillText(a.ftl.layer.label, 50, currTop - 10);
    } else {
      if (firstWithoutLabel) {
        currTop += 8
        firstWithoutLabel = false
      }

    }
    ctx.font = "20px Arial";
    if (a.img == "" || (a?.ftl?.layer && ("noLegend" in a.ftl.layer && a.ftl.layer.noLegend === true))) {
      return
    }

    ctx.filter = `hue-rotate(${a.ftl.layer.paint?.["raster-hue-rotate"] || 0}deg) saturate(${(a.ftl.layer.paint?.["raster-saturation"] || 0) + 1})`;
    ctx.drawImage(a.img,
      0, a.ftl.layer.legendOffset || 0, a.img.width, a.ftl.layer.legendCrop || a.img.height,
      25, currTop - 4, a.img.width * legendScaling, (a.ftl.layer.legendCrop || a.img.height) * legendScaling
    )
    if (a.label) {
      ctx.fillText(a.label, 25 + a.img.width * legendScaling + 2, currTop + ((a.ftl.layer.legendCrop || a.img.height) * legendScaling / 2) + 8)
    }
    currTop += ((a.ftl.layer.legendCrop || a.img.height) * legendScaling);
  })
}

function DrawSizeByValue(ctx: CanvasRenderingContext2D, currTop: number, colorByValue: any, layerInfo: LayerInfo) {
  if (!layerInfo.styling.sizeByProperty || !layerInfo.styling.sizeByValue?.divisions) {
    return 0
  }

  ctx.fillText(layerInfo.styling.sizeByProperty, 55, currTop + 20)
  let x = 25
  const increas = (layerInfo.styling.sizeByValue.useSizeRange || 0) % 2
  layerInfo.styling.sizeByValue?.divisions.list.forEach((a, idx, list) => {
    ctx.fillStyle = layerInfo.styling.color || "#000";
    ctx.beginPath()
    if (!increas) {
      let radius = (4 * (idx) + 7) / 2;
      ctx.arc(50 + radius, x + currTop + 12, radius, 0, 2 * Math.PI);
      ctx.fill();
    } else {
      let radius = (4 * (list.length - idx) + 7) / 2
      ctx.arc(50 + radius, x + currTop + 12, radius, 0, 2 * Math.PI);
      ctx.fill();
    }
    ctx.fillStyle = "#000"
    ctx.fillText(a.to?.toString() ? "<=" : " >", 90, currTop + 20 + x)
    ctx.fillText(a.to?.toString() || a.from?.toString() || "", 120, currTop + 20 + x)
    x += 25
  })
  return x
}

function DrawColorByValue(ctx: CanvasRenderingContext2D, currTop: number, colorByValue: any, layerInfo: LayerInfo) {
  if (!layerInfo.styling.colorByProperty || !layerInfo.styling.colorByValue?.divisions) {
    return 0
  }
  ctx.fillText(layerInfo.styling.colorByProperty, 55, currTop + 20)
  let x = 25
  layerInfo.styling.colorByValue?.divisions.list.forEach((a) => {
    ctx.fillStyle = a.color || "#000"
    ctx.fillRect(50, currTop + x, 20, 20)
    ctx.fillStyle = "#000"
    if (a.label) {
      ctx.fillText(a.label, 90, currTop + 20 + x)
    } else if (a.value) {
      ctx.fillText(a.value, 90, currTop + 20 + x)
    } else {
      ctx.fillText(a.to?.toString() ? "<=" : " >", 90, currTop + 20 + x)
      ctx.fillText(a.to?.toString() || a.from?.toString() || "", 120, currTop + 20 + x)
    }
    x += 25
  })
  return x
}

function DrawDataIcon(ctx: CanvasRenderingContext2D, currTop: number, icon: HTMLImageElement | "", layerInfo: LayerInfo) {
  if (icon == "") {
    return 0
  }
  ctx.font = "bold 18px Arial"
  ctx.drawImage(icon, 15, currTop + 2, 16, 22)
  ctx.fillText(layerInfo.datasetname, 50, 20 + currTop)
  return 25
}

function dataLayerLegendPinColor(layerInfo: LayerInfo): string {
  const jpbBufferId = getJpbBufferPointLegendLayerId();
  if (
    layerInfo.layerId === jpbBufferId &&
    (layerInfo.type === LayerType.GeoJSON_Point || layerInfo.type === LayerType.GeoJSON_PointRadius)
  ) {
    return JPB_BUFFER_POINT_PIN_COLOR;
  }
  return layerInfo.styling!.color || "#000";
}

async function dataLayerLegend(drawingOptions: PlotScreenState) {


  const layer = drawingOptions.dataLayer.filter((a) => a.visible)

  return await Promise.all(layer.map(async (layerInfo) => {



    const pinColor = dataLayerLegendPinColor(layerInfo);
    const layerIcon =
      layerInfo.type === LayerType.GeoJSON_Point ||
        layerInfo.type === LayerType.PointWGS84 ||
        layerInfo.type === LayerType.GeoJSON_PointRadius ? (
        await svgToHtmlImage(GenerateGeom.MitPinSvg(pinColor))
      ) : (
        await svgToHtmlImage(GenerateGeom.MitAreaSvg(layerInfo!.styling!.color))
      );
    let valueColorLegend = layerInfo!.styling!.colorByProperty
    let valueSizeLegend = layerInfo!.styling!.sizeByProperty
    let noDataLegend = MapitUtils.isAreaLayer(layerInfo.type) && layerInfo.styling.areaNoData?.show

    let areaFillColorLegendValue =
      MapitUtils.isAreaLayer(layerInfo.type) &&
      !layerInfo!.styling!.colorByProperty
    let otherValueDataLegendValue =
      layerInfo!.styling!.colorByValue &&
      layerInfo!.styling!.colorByValue!.divisions &&
      layerInfo!.styling!.colorByValue!.divisions!.type ===
      DataDivisionType.Discrete &&
      (layerInfo!.styling!.colorByValue!.divisions!.otherValuesCount || 0) > 0
    return {
      icon: layerIcon,
      name: layerInfo.datasetname,
      layerInfo: layerInfo,
      valueColorLegend: valueColorLegend,
      valueSizeLegend: valueSizeLegend,
      noDataLegend: noDataLegend,
      areaFillColorLegendValue: areaFillColorLegendValue,
      otherValueDataLegendValue: otherValueDataLegendValue,
      height: 30 +
        (layerInfo.styling.colorByValue?.divisions?.list?.length || 0) * 25 + (layerInfo.styling.colorByValue?.divisions?.list?.length ? 25 : 0) +
        (layerInfo.styling.sizeByValue?.divisions?.list?.length || 0) * 25 + (layerInfo.styling.sizeByValue?.divisions?.list?.length ? 25 : 0)
    }
  }))
}

function logoImage() {
  let url = SettingsManager.getSystemSetting("plot.image.url", "default", true) as string
  if (url === "default" || !url) {
    return new URL("/Mapit/MapitLogoTissegul.svg", import.meta.url).href
  }
  return url
}

async function svgToHtmlImage(svgString): Promise<HTMLImageElement | ""> {
  return new Promise((res) => {
    var svg = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8"
    });

    var url = URL.createObjectURL(svg);

    var img = new Image();
    img.onload = () => {
      res(img)
      URL.revokeObjectURL(url);
    }
    img.onerror = () => {
      res("")
    }
    img.width = 50;
    img.height = 82;
    img.src = url
  });
} 